import { ZIAMEZA_LINKS } from "./ziaMezaLinks";
import { isValidRoomCode, normalizeRoomCode } from "./game";

/** Canonical page paths live here; legacy query selectors remain inbound aliases. */
export const SITE_PATHS = {
    randomTeams: "/random-teams/",
    vocabularyReview: "/vocabulary-review/",
    wordList: "/word-list/",
    autotranslator: "/autotranslator/",
    account: "/account/",
    lyricLearn: "/lyric-learn/",
    school: "/school/",
    about: "/about/"
} as const;

export const APP_TOOL_PATHS = {
    "random-teams-orders": SITE_PATHS.randomTeams,
    "vocabulary-review": SITE_PATHS.vocabularyReview,
    "word-list": SITE_PATHS.wordList,
    autotranslator: SITE_PATHS.autotranslator
} as const;

export type AppTool = keyof typeof APP_TOOL_PATHS;

export const joinPagePath = (roomCode: string): string => `/join/${encodeURIComponent(roomCode)}/`;

export const APP_PAGE_METADATA = [
    { path: SITE_PATHS.randomTeams, title: "Random Teams / Orders", description: "Arrange students into random teams or a random order.", listed: true },
    { path: SITE_PATHS.vocabularyReview, title: "Vocabulary Review", description: "Paste a word list and review English and Korean together.", listed: true },
    { path: SITE_PATHS.wordList, title: "Word List Builder", description: "Build a classroom vocabulary list.", listed: false },
    { path: SITE_PATHS.autotranslator, title: "Autotranslator", description: "Ziarcadia account tool.", listed: false },
    { path: SITE_PATHS.account, title: "Account", description: "Your Ziarcadia account.", listed: false }
] as const;

export function appToolForPath(pathname: string): AppTool | null {
    return (Object.keys(APP_TOOL_PATHS) as AppTool[]).find(tool => APP_TOOL_PATHS[tool] === pathname) ?? null;
}

const toolAliases: Record<string, string> = {
    ...APP_TOOL_PATHS,
    "random-teams": SITE_PATHS.randomTeams,
    "music-class": SITE_PATHS.lyricLearn,
    "lyric-learn": SITE_PATHS.lyricLearn,
    "gimcheon-school": SITE_PATHS.school,
    master1000: ZIAMEZA_LINKS.master1000.href
};

const pageAliases: Record<string, string> = {
    "/index.html": "/",
    "/music-class": SITE_PATHS.lyricLearn,
    "/music-class/": SITE_PATHS.lyricLearn,
    "/music-class/index.html": SITE_PATHS.lyricLearn,
    "/random-teams-orders": SITE_PATHS.randomTeams,
    "/random-teams-orders/": SITE_PATHS.randomTeams,
    "/random-teams-orders/index.html": SITE_PATHS.randomTeams,
    "/gimcheon-school": SITE_PATHS.school,
    "/gimcheon-school/": SITE_PATHS.school
};
for (const path of Object.values(SITE_PATHS)) {
    pageAliases[path.slice(0, -1)] = path;
    pageAliases[`${path}index.html`] = path;
}
for (const [tool, path] of Object.entries(toolAliases)) {
    for (const suffix of ["", "/", "/index.html"]) pageAliases[`/tools/${tool}${suffix}`] = path;
}

/** Change page selectors only. Session parameters, tool settings and fragments survive. */
export function canonicalPageUrl(href: string): string | null {
    const url = new URL(href);
    const root = url.pathname === "/" || url.pathname === "/index.html";
    const tool = root ? url.searchParams.get("tool") : null;
    let target = pageAliases[url.pathname];
    if (root && tool && Object.hasOwn(toolAliases, tool)) {
        // Room links historically take precedence over app tools; music owns its own room URLs.
        if (!url.searchParams.has("room") || ["music-class", "lyric-learn", "master1000", "gimcheon-school"].includes(tool)) {
            target = toolAliases[tool];
            url.searchParams.delete("tool");
        }
    }
    if (root && url.searchParams.has("account") && !url.searchParams.has("room") && !tool) {
        target = SITE_PATHS.account;
        url.searchParams.delete("account");
    }
    if (root && !tool && !url.searchParams.has("room") && !url.searchParams.has("host") && !url.searchParams.has("controller")) {
        const join = normalizeRoomCode(url.searchParams.get("join") ?? "");
        if (isValidRoomCode(join)) {
            target = joinPagePath(join);
            url.searchParams.delete("join");
        }
    }
    // Preserve the existing external destination for retired embedded Master 1000 assets too.
    if (url.pathname.startsWith("/tools/master1000/")) target = ZIAMEZA_LINKS.master1000.href;
    const joinPath = url.pathname.match(/^\/join\/([A-Za-z0-9]+)\/?$/);
    if (joinPath && isValidRoomCode(normalizeRoomCode(joinPath[1]))) target = joinPagePath(normalizeRoomCode(joinPath[1]));
    if (!target) return null;
    const destination = new URL(target, url);
    destination.search = url.search;
    destination.hash = url.hash;
    return destination.href === href ? null : destination.href;
}

export function pageRedirect(request: Request): Response | null {
    if (request.method !== "GET" && request.method !== "HEAD") return null;
    const target = canonicalPageUrl(request.url);
    return target ? Response.redirect(target, 308) : null;
}
